import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel, { formControlLabelClasses } from '@mui/material/FormControlLabel'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { append, find, includes, innerJoin, map, pipe, propEq, without } from 'ramda'
import React from 'react'

import Loader from '@/app/component/atom/loader'
import CustomTooltip from '@/app/component/atom/tooltip'
import IconText from '@/app/component/layout/icon-text'
import CustomDialog from '@/app/component/wrapper/custom-dialog'
import ActionPreview from '@/app/module/campaigns/component/actions/action-preview'
import { displayMap } from '@/app/module/campaigns/definitions'
import { ActionSnippetType } from '@/app/module/campaigns/types'
import { PersonalizationType, SelectOption } from '@/app/types'

import { useActionSnippets } from '../../item/steps/content/types/multimessage/hooks/use-action-snippets'

type Props = {
  open: boolean
  personalizationList: PersonalizationType
  valuesList: SelectOption<string>[]

  onClose: () => void
  onInsert: (snippets: ActionSnippetType['snippet'][]) => void
}

type AddFromLibraryProps = {
  actionSnippets: ActionSnippetType[]
  loading: boolean
  personalizationList: PersonalizationType
  valuesList: SelectOption<string>[]

  onInsert: (snippets: ActionSnippetType['snippet'][]) => void
}

const AddFromLibrary: React.FC<AddFromLibraryProps> = ({
  actionSnippets,
  loading,
  personalizationList,
  valuesList,
  onInsert,
}) => {
  const [selected, setSelected] = React.useState<number[]>([])

  return (
    <>
      <DialogContent>
        {loading && <Loader />}
        {!loading && (
          <>
            {actionSnippets.length === 0 && (
              <Typography variant="h6" style={{ textAlign: 'center' }}>
                You didn't add anything to your library yet. Copy any action to reuse them anywhere in the application.
              </Typography>
            )}
            {actionSnippets.map((snippet) => {
              const validAction = find(propEq('value', snippet.snippet.type), valuesList)
              return (
                <div
                  key={snippet.id}
                  style={{
                    width: '540px',
                    display: 'flex',
                  }}
                >
                  <CustomTooltip title={validAction ? '' : 'This action is not valid for this part type.'}>
                    <StyledFormControlLabel
                      control={
                        <Checkbox
                          disabled={!validAction}
                          checked={includes(snippet.id, selected)}
                          onChange={() => {
                            if (includes(snippet.id, selected)) {
                              setSelected(without([snippet.id], selected))
                            } else {
                              setSelected(append(snippet.id, selected))
                            }
                          }}
                        />
                      }
                      label={
                        <ActionPreview
                          action={snippet.snippet}
                          personalizationList={personalizationList}
                          displayMap={displayMap}
                        />
                      }
                      style={{ display: 'flex', flex: 1 }}
                    />
                  </CustomTooltip>
                </div>
              )
            })}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          disabled={selected.length === 0}
          onClick={() => onInsert(getSelectedSnippets(actionSnippets, selected))}
        >
          Insert
        </Button>
      </DialogActions>
    </>
  )
}

const AddFromLibraryDialog: React.FC<Props> = ({ open, personalizationList, valuesList, onClose, onInsert }) => {
  const { data: actionSnippets, isFetching, isLoading, isUninitialized } = useActionSnippets()

  if (!open) {
    return null
  }

  const loading = isFetching || isLoading || isUninitialized

  return (
    <CustomDialog fullWidth maxWidth="sm" open onClose={onClose}>
      <DialogTitle style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <IconText>
            <Icon>library_add</Icon>
            <Typography variant="h6">Add from library</Typography>
          </IconText>
          <IconButton onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </div>
      </DialogTitle>
      <AddFromLibrary
        actionSnippets={actionSnippets}
        loading={loading}
        personalizationList={personalizationList}
        valuesList={valuesList}
        onInsert={(snippets) => {
          onInsert(snippets)
          onClose()
        }}
      />
    </CustomDialog>
  )
}

const getSelectedSnippets = (snippets: ActionSnippetType[], selected: number[]) =>
  pipe(
    innerJoin((snippet: ActionSnippetType, id: number) => snippet.id === id, snippets),
    map((snippet: ActionSnippetType) => snippet.snippet),
  )(selected)

const StyledFormControlLabel = styled(FormControlLabel)(() => ({
  [`& .${formControlLabelClasses.label}`]: {
    flex: 1,
  },
}))

export default AddFromLibraryDialog
